import React, { useState, useEffect } from 'react';
import { QuestionDefinition, QuestionType } from '../../types/survey.types';
import BasicSettings from './editor/BasicSettings';
import AdvancedSettings from './editor/AdvancedSettings';
import OptionsSettings from './editor/OptionsSettings';
import QuestionPreview from './editor/QuestionPreview';
import LogicSettings from './editor/LogicSettings';
import { useToast } from '../common/Toast';
import ConfirmDialog from '../common/ConfirmDialog';
import { deepClone } from '../../utils/deepClone';

interface QuestionEditorModalProps {
  question: QuestionDefinition;
  isOpen: boolean;
  onClose: () => void;
  onSave: (question: QuestionDefinition) => void;
  onDelete: (questionId: string) => void;
  questionIcon: string;
  allQuestions?: QuestionDefinition[]; // All questions in the survey for logic rules
}

type TabType = 'basic' | 'options' | 'advanced' | 'preview' | 'logic';

const QuestionEditorModal: React.FC<QuestionEditorModalProps> = ({
  question: initialQuestion,
  isOpen,
  onClose,
  onSave,
  onDelete,
  questionIcon,
  allQuestions = [],
}) => {
  const toast = useToast();
  const [question, setQuestion] = useState<QuestionDefinition>(initialQuestion);
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);

  // Only reset state when modal opens (isOpen changes from false to true)
  const [wasOpen, setWasOpen] = React.useState(false);

  useEffect(() => {
    if (isOpen && !wasOpen) {
      // Modal just opened - deep clone to preserve nested arrays like logicRules and Date objects
      setQuestion(deepClone(initialQuestion));
      setHasUnsavedChanges(false);
      setActiveTab('basic');
      setShowDeleteConfirm(false);
      setShowUnsavedConfirm(false);
    }
    setWasOpen(isOpen);
  }, [isOpen, wasOpen, initialQuestion]);

  useEffect(() => {
    if (JSON.stringify(question) !== JSON.stringify(initialQuestion)) {
      setHasUnsavedChanges(true);
    }
  }, [question, initialQuestion]);

  const shouldShowOptionsTab = false; // Options are now in Basic tab
  const shouldShowAdvancedTab = !['yesno', 'dropdown'].includes(question.type);

  const tabs: { id: TabType; label: string; visible: boolean; disabled?: boolean; icon?: string }[] = [
    { id: 'basic', label: 'Basic', visible: true, icon: '📝' },
    { id: 'options', label: 'Options', visible: shouldShowOptionsTab, icon: '☑️' },
    { id: 'advanced', label: 'Advanced', visible: shouldShowAdvancedTab, icon: '⚙️' },
    { id: 'preview', label: 'Preview', visible: true, icon: '👁️' },
    { id: 'logic', label: 'Logic', visible: true, disabled: false, icon: '⚡' },
  ];

  const questionTypeOptions: { value: QuestionType; label: string }[] = [
    { value: 'text', label: 'Single Line Text' },
    { value: 'textarea', label: 'Multi-Line Text Area' },
    { value: 'number', label: 'Number Input' },
    { value: 'email', label: 'Email Address' },
    { value: 'phone', label: 'Phone Number' },
    { value: 'date', label: 'Date Picker' },
    { value: 'radio', label: 'Multiple Choice (Radio)' },
    { value: 'checkbox', label: 'Checkboxes (Multi-Select)' },
    { value: 'dropdown', label: 'Dropdown Menu' },
    { value: 'multiselect', label: 'Multi-Select Dropdown' },
    { value: 'rating', label: 'Star Rating' },
    { value: 'scale', label: 'Likert Scale' },
    { value: 'yesno', label: 'Yes/No Question' },
    { value: 'file', label: 'File Upload' },
    { value: 'signature', label: 'Digital Signature' },
    { value: 'matrix', label: 'Matrix/Grid' },
    { value: 'ranking', label: 'Ranking' },
    { value: 'dosing_schedule', label: 'Dosing Schedule' },
    { value: 'measurement', label: 'Dose / Strength' },
  ];

  const handleTypeChange = (newType: QuestionType) => {
    setQuestion(prev => {
      const updated = {
        ...prev,
        type: newType
      };

      // Clear options if changing to a type that doesn't use them
      if (!['radio', 'checkbox', 'dropdown', 'multiselect', 'yesno', 'ranking'].includes(newType)) {
        delete updated.options;
      }

      // Add default options for yesno if changing to that type
      if (newType === 'yesno' && (!updated.options || updated.options.length === 0)) {
        updated.options = [
          { id: 'yes', label: 'Yes', value: 'yes' },
          { id: 'no', label: 'No', value: 'no' },
        ];
      }

      // Clear logic rules that reference options that no longer exist
      if (updated.logicRules && updated.logicRules.length > 0) {
        // If the new type doesn't have options, clear all logic rules
        if (!updated.options) {
          updated.logicRules = [];
        } else {
          // Filter out logic rules that have conditions referencing non-existent option values
          const validOptionValues = new Set(updated.options.map(opt => opt.value));
          updated.logicRules = updated.logicRules.filter(rule => {
            // Check if all conditions in all rule groups reference valid options
            return rule.ruleGroups.every(ruleGroup =>
              ruleGroup.conditions.every(condition => {
                // If condition doesn't have a value, keep it
                if (!condition.value) return true;
                // For conditions with values, check if the value exists in current options
                if (Array.isArray(condition.value)) {
                  return condition.value.some((v: string) => validOptionValues.has(v));
                }
                return validOptionValues.has(String(condition.value));
              })
            );
          });
        }
      }

      return updated;
    });
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedConfirm(true);
    } else {
      onClose();
    }
  };

  const handleUnsavedConfirm = () => {
    setShowUnsavedConfirm(false);
    onClose();
  };

  const handleSave = () => {
    if (!question.title.trim()) {
      toast.warning('Please enter a question title');
      setActiveTab('basic');
      return;
    }

    if (['radio', 'checkbox', 'dropdown'].includes(question.type)) {
      if (!question.options || question.options.length < 2) {
        toast.warning('Please add at least 2 options');
        setActiveTab('options');
        return;
      }
    }

    if (question.type === 'number') {
      const min = question.settings?.minValue;
      const max = question.settings?.maxValue;
      if (min !== undefined && max !== undefined && min > max) {
        toast.error('Minimum value cannot be greater than maximum value');
        setActiveTab('advanced');
        return;
      }
    }

    onSave(question);
    setHasUnsavedChanges(false);
    toast.success('Question saved successfully!');
  };

  const handleDeleteClick = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    onDelete(question.id);
    toast.success('Question deleted');
    setShowDeleteConfirm(false);
    onClose();
  };

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasUnsavedChanges]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 overflow-y-auto"
        style={{ backgroundColor: 'rgba(15, 27, 45, 0.6)', backdropFilter: 'blur(4px)' }}
        onClick={(e) => {
          // Only close if clicking directly on the backdrop, not on child elements
          if (e.target === e.currentTarget) {
            handleClose();
          }
        }}
      >
        <div className="min-h-screen flex items-center justify-center p-4">
          <div
            className="relative w-full max-w-3xl max-h-[80vh] flex flex-col"
            style={{
              background: 'var(--glass-bg-card)',
              borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--glass-border-light)',
              boxShadow: 'var(--shadow-xl)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header - Glassmorphism Design */}
          <div className="flex-shrink-0 px-6 py-4" style={{
            background: 'var(--glass-bg-subtle)',
            borderBottom: '1px solid var(--glass-border-light)',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)'
          }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1">
                {/* Icon with accent gradient */}
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{
                  background: 'var(--gradient-accent-btn)',
                  boxShadow: '0 4px 8px rgba(199, 161, 74, 0.2)'
                }}>
                  {questionIcon}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    color: 'var(--color-primary)',
                    marginBottom: '0.25rem'
                  }}>
                    Edit Question
                  </h2>
                  <div className="flex items-center gap-2">
                    <label style={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'var(--color-muted)',
                      fontFamily: 'var(--font-body)'
                    }}>
                      Type:
                    </label>
                    <select
                      value={question.type}
                      onChange={(e) => handleTypeChange(e.target.value as QuestionType)}
                      className="px-2.5 py-1 rounded-md transition-all"
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: 'var(--color-primary)',
                        border: '1px solid var(--glass-border-light)',
                        backgroundColor: 'white',
                        maxWidth: '220px'
                      }}
                    >
                      {questionTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="rounded-lg p-2 transition-all"
                style={{
                  color: 'var(--color-muted)',
                  backgroundColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.05)';
                  e.currentTarget.style.color = 'var(--color-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-muted)';
                }}
                title="Close (ESC)"
                aria-label="Close modal"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Tabs - Glassmorphism Design */}
          <div className="flex-shrink-0 px-4 py-2" style={{
            backgroundColor: 'var(--glass-bg-subtle)',
            borderBottom: '1px solid var(--glass-border-light)'
          }}>
            <div className="flex gap-2 overflow-x-auto">
              {tabs
                .filter((tab) => tab.visible)
                .map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => !tab.disabled && setActiveTab(tab.id)}
                    disabled={tab.disabled}
                    className="relative px-4 py-2 text-sm transition-all whitespace-nowrap rounded-lg"
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontWeight: 600,
                      background: activeTab === tab.id ? 'var(--glass-bg-card)' : 'transparent',
                      color: activeTab === tab.id ? 'var(--color-primary)' : tab.disabled ? 'var(--color-muted)' : 'var(--color-muted)',
                      cursor: tab.disabled ? 'not-allowed' : 'pointer',
                      opacity: tab.disabled ? 0.5 : 1,
                      border: activeTab === tab.id ? '1px solid var(--color-accent)' : '1px solid transparent',
                      boxShadow: activeTab === tab.id ? 'var(--glass-shadow-sm)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (!tab.disabled && activeTab !== tab.id) {
                        e.currentTarget.style.background = 'rgba(199, 161, 74, 0.08)';
                        e.currentTarget.style.color = 'var(--color-primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeTab !== tab.id) {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = tab.disabled ? 'var(--color-muted)' : 'var(--color-muted)';
                      }
                    }}
                  >
                    <span className="flex items-center gap-1.5">
                      <span style={{ fontSize: '0.875rem' }}>{tab.icon}</span>
                      <span>{tab.label}</span>
                    </span>
                  </button>
                ))}
            </div>
          </div>

          {/* Content Area - Scrollable */}
          <div className="flex-1 overflow-y-auto px-6 py-5" style={{ background: 'white' }}>
            <div className="space-y-5">
            {activeTab === 'basic' && (
              <BasicSettings question={question} onChange={setQuestion} />
            )}

            {activeTab === 'options' && shouldShowOptionsTab && (
              <OptionsSettings question={question} onChange={setQuestion} />
            )}

            {activeTab === 'advanced' && (
              <AdvancedSettings question={question} onChange={setQuestion} />
            )}

            {activeTab === 'preview' && (
              <QuestionPreview question={question} />
            )}

            {activeTab === 'logic' && (
              <LogicSettings
                question={question}
                allQuestions={allQuestions}
                onChange={setQuestion}
              />
            )}
            </div>
          </div>

          {/* Footer - Glassmorphism Design */}
          <div className="flex-shrink-0 px-6 py-4" style={{
            background: 'var(--glass-bg-subtle)',
            borderTop: '1px solid var(--glass-border-light)',
            borderBottomLeftRadius: 'var(--radius-xl)',
            borderBottomRightRadius: 'var(--radius-xl)'
          }}>
            <div className="flex justify-between items-center">
              <button
                onClick={handleDeleteClick}
                className="glass-btn-danger-soft px-4 py-2 rounded-lg transition-all flex items-center gap-2"
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete Question
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleClose}
                  className="px-5 py-2 rounded-lg transition-all"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--color-primary)',
                    background: 'var(--glass-bg-card)',
                    border: '1px solid var(--glass-border-light)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--color-muted)';
                    e.currentTarget.style.boxShadow = 'var(--glass-shadow-sm)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--glass-border-light)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 rounded-lg transition-all flex items-center gap-2"
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    background: 'var(--gradient-accent-btn)',
                    color: 'var(--color-primary)',
                    border: '1px solid var(--color-accent)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(199, 161, 74, 0.3)'}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Question"
        message="Are you sure you want to delete this question? This action cannot be undone and will remove the question from your survey."
        confirmText="Delete Question"
        cancelText="Keep Question"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        type="danger"
      />

      {/* Unsaved Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showUnsavedConfirm}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close without saving? All changes will be lost."
        confirmText="Discard Changes"
        cancelText="Keep Editing"
        onConfirm={handleUnsavedConfirm}
        onCancel={() => setShowUnsavedConfirm(false)}
        type="warning"
      />
    </>
  );
};

export default QuestionEditorModal;